import { useNotification } from '@refinedev/core'
import type { AdminLanguage } from '@sirius/contracts/admin'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { errorMessage } from '~/providers/errors'
import { ME_QUERY_KEY } from '~/providers/sessionQueries'
import { meInput } from '~/tenancy/hostSlug'
import { useCachedSession } from './panelLanguage'

/**
 * The signed-in user's own panel language: their choice (null: Use default),
 * what Use default means for them today, and the setter. A save stores the
 * answer in the `auth.me` cache, so `PanelLanguageProvider` switches the
 * panel to it; a failed save says so and leaves the language as it was.
 */
export function useMyLanguage() {
  const client = useAdminClient()
  const queryClient = useQueryClient()
  const session = useCachedSession(queryClient)
  const { t } = useTranslation()
  const { open } = useNotification()

  const mutation = useMutation({
    mutationFn: (language: AdminLanguage | null) =>
      client.auth.setMyLanguage({ language, ...meInput() }),
    onSuccess: (updated) => {
      queryClient.setQueryData(ME_QUERY_KEY, updated)
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('shell.userMenu.language.saveFailed', {
          message: errorMessage(error) ?? error.message,
        }),
      })
    },
  })

  return {
    /** null: Use default; undefined until the session has loaded */
    language: session?.language,
    inheritedLanguage: session?.inheritedLanguage,
    setLanguage: mutation.mutate,
    saving: mutation.isPending,
  }
}
